import React, { useState, useEffect } from 'react';
import { 
  Search, Filter, Download, ChevronLeft, ChevronRight, 
  Send, Mail, Inbox, Archive, Star, Trash2, Reply, 
  MoreVertical, Paperclip, Smile, Image, File,
  User, Phone, Calendar, Clock, CheckCircle, XCircle,
  AlertCircle, Plus, Edit, Eye, MessageSquare, Users,
  Tag, Flag, Printer, Share2, Bell, Volume2, VolumeX,
  Headphones, DollarSign, TrendingUp
} from 'lucide-react';
import { Link } from 'react-router-dom';

const Messages = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [showComposeModal, setShowComposeModal] = useState(false);
  const [showReplyModal, setShowReplyModal] = useState(false);
  const [activeTab, setActiveTab] = useState('inbox');
  const itemsPerPage = 6;

  // Static Mock Data
  const [messages, setMessages] = useState([
    {
      id: 'MSG-001',
      from: { name: 'Rahul Sharma', avatar: 'R', email: 'rahul@example.com', role: 'Customer' },
      to: 'Admin',
      subject: 'Order Delivery Issue',
      message: 'My order #ORD-7829 has not been delivered yet. It shows delivered but I haven\'t received it.',
      date: 'Oct 24, 2024',
      time: '10:30 AM',
      status: 'unread',
      type: 'inquiry',
      priority: 'high',
      attachment: false,
      read: false
    },
    {
      id: 'MSG-002',
      from: { name: 'Green Thumb Nursery', avatar: 'G', email: 'greenthumb@example.com', role: 'Seller' },
      to: 'Admin',
      subject: 'Seller Account Verification',
      message: 'Please verify my seller account. I have submitted all required documents.',
      date: 'Oct 23, 2024',
      time: '02:15 PM',
      status: 'read',
      type: 'support',
      priority: 'medium',
      attachment: true,
      read: true
    },
    {
      id: 'MSG-003',
      from: { name: 'Priya Patel', avatar: 'P', email: 'priya@example.com', role: 'Customer' },
      to: 'Admin',
      subject: 'Payment Refund Request',
      message: 'I would like to request a refund for my order #ORD-7832. The plant arrived damaged.',
      date: 'Oct 22, 2024',
      time: '09:45 AM',
      status: 'read',
      type: 'refund',
      priority: 'high',
      attachment: true,
      read: true
    },
    {
      id: 'MSG-004',
      from: { name: 'City Gardens', avatar: 'C', email: 'citygardens@example.com', role: 'Seller' },
      to: 'Admin',
      subject: 'Payout Status Inquiry',
      message: 'When will my October payout be processed? I haven\'t received any update.',
      date: 'Oct 21, 2024',
      time: '11:20 AM',
      status: 'unread',
      type: 'payout',
      priority: 'medium',
      attachment: false,
      read: false
    },
    {
      id: 'MSG-005',
      from: { name: 'Amit Singh', avatar: 'A', email: 'amit@example.com', role: 'Customer' },
      to: 'Admin',
      subject: 'Product Quality Complaint',
      message: 'The Monstera plant I received is not as described. The leaves are yellowing.',
      date: 'Oct 20, 2024',
      time: '04:30 PM',
      status: 'unread',
      type: 'complaint',
      priority: 'high',
      attachment: true,
      read: false
    },
    {
      id: 'MSG-006',
      from: { name: 'Herbals Co.', avatar: 'H', email: 'herbals@example.com', role: 'Seller' },
      to: 'Admin',
      subject: 'New Product Listing Approval',
      message: 'Please approve my new product listings for medicinal plants.',
      date: 'Oct 19, 2024',
      time: '01:00 PM',
      status: 'read',
      type: 'approval',
      priority: 'low',
      attachment: false,
      read: true
    },
  ]);

  const stats = {
    total: messages.length,
    unread: messages.filter(m => !m.read).length,
    read: messages.filter(m => m.read).length,
    highPriority: messages.filter(m => m.priority === 'high').length,
    customers: messages.filter(m => m.from.role === 'Customer').length,
    sellers: messages.filter(m => m.from.role === 'Seller').length,
  };

  const getPriorityConfig = (priority) => {
    switch (priority) {
      case 'high': return { label: 'High', icon: AlertCircle, color: '#ef4444', bg: '#fee2e2' };
      case 'medium': return { label: 'Medium', icon: AlertCircle, color: '#f59e0b', bg: '#fed7aa' };
      default: return { label: 'Low', icon: AlertCircle, color: '#10b981', bg: '#d1fae5' };
    }
  };

  const getTypeConfig = (type) => {
    switch (type) {
      case 'inquiry': return { label: 'Inquiry', icon: MessageSquare, color: '#3b82f6', bg: '#dbeafe' };
      case 'support': return { label: 'Support', icon: Headphones, color: '#8b5cf6', bg: '#e9d5ff' };
      case 'refund': return { label: 'Refund', icon: DollarSign, color: '#ef4444', bg: '#fee2e2' };
      case 'payout': return { label: 'Payout', icon: TrendingUp, color: '#10b981', bg: '#d1fae5' };
      case 'complaint': return { label: 'Complaint', icon: Flag, color: '#f59e0b', bg: '#fed7aa' };
      case 'approval': return { label: 'Approval', icon: CheckCircle, color: '#8b5cf6', bg: '#e9d5ff' };
      default: return { label: type, icon: Mail, color: '#64748b', bg: '#f1f5f9' };
    }
  };

  const filteredMessages = messages.filter(message => {
    const matchesSearch = message.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          message.from.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          message.message.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || message.type === filterType;
    const matchesTab = activeTab === 'all' || 
                       (activeTab === 'unread' && !message.read) ||
                       (activeTab === 'read' && message.read);
    return matchesSearch && matchesType && matchesTab;
  });

  const totalPages = Math.ceil(filteredMessages.length / itemsPerPage);
  const paginatedMessages = filteredMessages.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const StatCard = ({ title, value, icon: Icon, color, bgColor }) => (
    <div className="stat-card">
      <div className="stat-icon" style={{ backgroundColor: bgColor }}>
        <Icon size={20} color={color} />
      </div>
      <div className="stat-info">
        <h3>{value}</h3>
        <span>{title}</span>
      </div>
    </div>
  );

  const handleMarkAsRead = (id) => {
    setMessages(messages.map(msg => 
      msg.id === id ? { ...msg, read: true, status: 'read' } : msg
    ));
  };

  const handleDelete = (id) => {
    if (window.confirm('Delete this message?')) {
      setMessages(messages.filter(msg => msg.id !== id));
    }
  };

  const handleReply = (message) => {
    setSelectedMessage(message);
    setShowReplyModal(true);
  };

  const formatTimeAgo = (date, time) => {
    return `${date} at ${time}`;
  };

  return (
    <div className="admin-messages-modern">
      {/* Header */}
      <div className="messages-header">
        <div>
          <h1 className="page-title">Messages</h1>
          <p className="page-subtitle">Manage customer and seller communications</p>
        </div>
        <div className="header-actions">
          <button className="btn-export">
            <Download size={16} />
            Export
          </button>
          <button className="btn-primary" onClick={() => setShowComposeModal(true)}>
            <Plus size={16} />
            Compose Message
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <StatCard title="Total Messages" value={stats.total} icon={Mail} color="#10b981" bgColor="#d1fae5" />
        <StatCard title="Unread" value={stats.unread} icon={MessageSquare} color="#3b82f6" bgColor="#dbeafe" />
        <StatCard title="High Priority" value={stats.highPriority} icon={AlertCircle} color="#ef4444" bgColor="#fee2e2" />
        <StatCard title="Customers" value={stats.customers} icon={User} color="#8b5cf6" bgColor="#e9d5ff" />
      </div>

      {/* Tabs */}
      <div className="messages-tabs">
        <button className={`tab-btn ${activeTab === 'all' ? 'active' : ''}`} onClick={() => setActiveTab('all')}>
          <Inbox size={16} />
          All Messages
        </button>
        <button className={`tab-btn ${activeTab === 'unread' ? 'active' : ''}`} onClick={() => setActiveTab('unread')}>
          <Mail size={16} />
          Unread
          {stats.unread > 0 && <span className="tab-count">{stats.unread}</span>}
        </button>
        <button className={`tab-btn ${activeTab === 'read' ? 'active' : ''}`} onClick={() => setActiveTab('read')}>
          <Archive size={16} />
          Read
        </button>
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <div className="search-box">
          <Search size={18} />
          <input 
            type="text" 
            placeholder="Search by subject, sender or content..." 
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <select className="filter-select" value={filterType} onChange={(e) => setFilterType(e.target.value)}>
            <option value="all">All Types</option>
            <option value="inquiry">Inquiry</option>
            <option value="support">Support</option>
            <option value="refund">Refund</option>
            <option value="payout">Payout</option>
            <option value="complaint">Complaint</option>
            <option value="approval">Approval</option>
          </select>
        </div>
      </div>

      {/* Messages List */}
      <div className="messages-container">
        {/* Messages Grid */}
        <div className="messages-grid">
          {paginatedMessages.map((message) => {
            const priorityConfig = getPriorityConfig(message.priority);
            const typeConfig = getTypeConfig(message.type);
            const PriorityIcon = priorityConfig.icon;
            const TypeIcon = typeConfig.icon;
            
            return (
              <div key={message.id} className={`message-card ${!message.read ? 'unread' : ''}`}>
                <div className="message-card-header">
                  <div className="sender-info">
                    <div className="sender-avatar">
                      <span>{message.from.avatar}</span>
                    </div>
                    <div>
                      <h4 className="sender-name">{message.from.name}</h4>
                      <div className="sender-role">{message.from.role}</div>
                    </div>
                  </div>
                  <div className="message-badges">
                    <span className="type-badge" style={{ backgroundColor: typeConfig.bg, color: typeConfig.color }}>
                      <TypeIcon size={12} />
                      {typeConfig.label}
                    </span>
                    <span className="priority-badge" style={{ backgroundColor: priorityConfig.bg, color: priorityConfig.color }}>
                      <PriorityIcon size={12} />
                      {priorityConfig.label}
                    </span>
                  </div>
                </div>
                
                <div className="message-card-body">
                  <h5 className="message-subject">{message.subject}</h5>
                  <p className="message-preview">{message.message.substring(0, 120)}...</p>
                  <div className="message-meta">
                    <div className="meta-item">
                      <Mail size={12} />
                      <span>{message.from.email}</span>
                    </div>
                    <div className="meta-item">
                      <Clock size={12} />
                      <span>{formatTimeAgo(message.date, message.time)}</span>
                    </div>
                    {message.attachment && (
                      <div className="meta-item attachment">
                        <Paperclip size={12} />
                        <span>Has attachment</span>
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="message-card-footer">
                  {!message.read && (
                    <button className="action-btn read" onClick={() => handleMarkAsRead(message.id)}>
                      <Eye size={14} />
                      Mark as Read
                    </button>
                  )}
                  <button className="action-btn reply" onClick={() => handleReply(message)}>
                    <Reply size={14} />
                    Reply
                  </button>
                  <button className="action-btn delete" onClick={() => handleDelete(message.id)}>
                    <Trash2 size={14} />
                    Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="pagination-wrapper">
            <div className="pagination-info">
              Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredMessages.length)} of {filteredMessages.length} messages
            </div>
            <div className="pagination-buttons">
              <button 
                className={`page-btn ${currentPage === 1 ? 'disabled' : ''}`} 
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft size={16} /> Previous
              </button>
              {[...Array(totalPages)].map((_, i) => (
                <button 
                  key={i}
                  className={`page-number ${currentPage === i + 1 ? 'active' : ''}`}
                  onClick={() => setCurrentPage(i + 1)}
                >
                  {i + 1}
                </button>
              ))}
              <button 
                className={`page-btn ${currentPage === totalPages ? 'disabled' : ''}`} 
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
              >
                Next <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Compose Message Modal */}
      {showComposeModal && (
        <div className="modal-overlay" onClick={() => setShowComposeModal(false)}>
          <div className="modal-container compose" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Compose New Message</h3>
              <button className="modal-close" onClick={() => setShowComposeModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>To</label>
                <input type="text" className="form-input" placeholder="Select recipient..." />
              </div>
              <div className="form-group">
                <label>Subject</label>
                <input type="text" className="form-input" placeholder="Enter subject..." />
              </div>
              <div className="form-group">
                <label>Message</label>
                <textarea className="form-textarea" rows="6" placeholder="Type your message here..."></textarea>
              </div>
              <div className="form-group attachment-area">
                <div className="attachment-upload">
                  <Paperclip size={20} />
                  <span>Attach files</span>
                  <small>or drag and drop</small>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowComposeModal(false)}>Cancel</button>
              <button className="btn-primary">Send Message</button>
            </div>
          </div>
        </div>
      )}

      {/* Reply Modal */}
      {showReplyModal && selectedMessage && (
        <div className="modal-overlay" onClick={() => setShowReplyModal(false)}>
          <div className="modal-container reply" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Reply to {selectedMessage.from.name}</h3>
              <button className="modal-close" onClick={() => setShowReplyModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="original-message">
                <div className="original-header">
                  <strong>Original Message:</strong>
                  <span>{selectedMessage.subject}</span>
                </div>
                <p>{selectedMessage.message}</p>
              </div>
              <div className="form-group">
                <label>Your Reply</label>
                <textarea className="form-textarea" rows="5" placeholder="Type your reply..."></textarea>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowReplyModal(false)}>Cancel</button>
              <button className="btn-primary">Send Reply</button>
            </div>
          </div>
        </div>
      )}

      
    </div>
  );
};

export default Messages;